import { Button, CloseIcon } from 'fronton-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breakpoint } from 'types/utils';
import { jcm } from 'utils';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { PictureSources } from '../photos-modal.types';
import {
  closeButton,
  contentPicture,
  layout,
  photoViewport,
  photoViewportMedium,
  rightContainer,
  rightContainerMedium,
  thumbsContainer,
  thumbsContainerMedium,
  toNextPhotoContainer,
  toPrevPhotoContainer,
} from './modal-layout-desktops.styles';
import { ModalThumbs } from './modal-thumbs';
import { pictureThatIsSameAs } from './utils';
import { ModalNavigationButton } from './modal-navigation-button';

type ModalLayoutDesktopProps = {
  breakpoint: Breakpoint;
  photosThumbs: PictureSources[];
  showedPicture: PictureSources | null;
  isCheckedGallery: boolean;
  hasMorePhotos?: boolean;
  onClose?: () => void;
  setShowedPicture?: (pic: PictureSources | null) => void;
  onRequestMorePhotos?: () => void;
};

export const ModalLayoutDesktop: React.FC<ModalLayoutDesktopProps> = ({
  breakpoint,
  photosThumbs,
  showedPicture,
  onClose,
  setShowedPicture,
  onRequestMorePhotos = () => null,
  hasMorePhotos = false,
  isCheckedGallery = false,
}) => {
  const { t } = useTranslation();

  const idx = photosThumbs.findIndex(pictureThatIsSameAs(showedPicture));
  const isShowToPrevBtn = idx > 0;
  const isShowToNextBtn = idx >= 0 && (idx < photosThumbs.length - 1 || (isCheckedGallery && hasMorePhotos));
  const isShowThumbs = idx >= 0;

  const showNextPictureCb = React.useCallback(() => {
    if (idx < 0) return;
    const isLast = idx >= photosThumbs.length - 1;

    if (isLast) {
      if (hasMorePhotos) {
        onRequestMorePhotos?.();
      }
      return;
    }
    setShowedPicture?.(photosThumbs[idx + 1]);
  }, [photosThumbs, idx, setShowedPicture, hasMorePhotos, onRequestMorePhotos]);

  const showPrevPictureCb = React.useCallback(() => {
    if (idx > 0) {
      setShowedPicture?.(photosThumbs[idx - 1]);
    }
  }, [photosThumbs, idx, setShowedPicture]);

  return (
    <div className={layout}>
      <div
        className={jcm({
          [thumbsContainer]: true,
          [thumbsContainerMedium]: breakpoint === 'medium',
        })}
      >
        {isShowThumbs && (
          <ModalThumbs
            breakpoint={breakpoint}
            showedPicIdx={idx}
            photosThumbs={photosThumbs}
            setShowedPicture={setShowedPicture}
            hasMorePhotos={hasMorePhotos}
            isCheckedGallery={isCheckedGallery}
            onRequestMorePhotos={onRequestMorePhotos}
          />
        )}

        <ModalNavigationButton
          direction="left"
          isVisible={isShowToPrevBtn}
          onClick={showPrevPictureCb}
          className={toPrevPhotoContainer}
        />
      </div>

      <section
        className={jcm({
          [photoViewport]: true,
          [photoViewportMedium]: breakpoint === 'medium',
        })}
        aria-label={t('ui.image.alt')}
      >
        <ImagePlaceholder
          className={contentPicture}
          src={showedPicture?.urlOriginal || ''}
          loading="lazy"
          alt=""
          fallbackAlt={t('ui.image.alt')}
          fallbackWidth={800}
          fallbackHeight={800}
        />
      </section>

      <div
        className={jcm({
          [rightContainer]: true,
          [rightContainerMedium]: breakpoint === 'medium',
        })}
      >
        <ModalNavigationButton
          direction="right"
          isVisible={isShowToNextBtn}
          onClick={showNextPictureCb}
          className={toNextPhotoContainer}
        />
      </div>

      <Button
        data-qa="modal-close"
        className={closeButton}
        kind="regular"
        variant="secondary"
        size="l"
        aria-label={t('ui.buttons.close')}
        onClick={onClose}
        rounded
        iconOnly
      >
        <CloseIcon />
      </Button>
    </div>
  );
};
